import { IPromise } from 'angular';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { TabActivationMode, TabAndPanelContents, TabStyles, TabsWidget, TextFieldProps, TextFieldWidget, } from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ListResult } from '../../ts/fetchers/ListResult';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { Filter } from '../../ts/helpers/Filter';
import { LockUnlockPermissionsState, NestedProjectsPermissionsState } from '../../ts/helpers/LockStatusHelper';
import { PermissionsEditability, PermissionsEditabilityLogic } from '../../ts/helpers/permissions/PermissionsEditabilityLogic';
import { PermissionsEditabilityLogicFactory } from '../../ts/helpers/permissions/PermissionsEditabilityLogicFactory';
import { PermissionsHelper } from '../../ts/helpers/PermissionsHelper';
import { L10n } from '../../ts/L10n/L10n';
import { DatabaseContentType, DataRoleContentType, DatasourceContentType, FlowContentType, MetricContentType, ProjectContentType, TableContentType, ViewContentType, WorkbookContentType } from '../../ts/models/ContentTypes';
import { IGranteePermissions } from '../../ts/models/IGranteePermissions';
import { IProjectInfo } from '../../ts/models/IProjectInfo';
import { IPermission, Permission, PermissionReason } from '../../ts/models/Permission';
import { PermissionRoles, SpecialRoleNames } from '../../ts/models/PermissionRoles';
import { Set } from '../../ts/models/Set';
import { Databases } from '../../ts/resources/Databases';
import { ExplicitPermissions, ExplicitPermissionsResult } from '../../ts/resources/ExplicitPermissions';
import { Projects } from '../../ts/resources/Projects';
import { SiteUsers } from '../../ts/resources/SiteUsers';
import { GetUsersGroupMembershipRequest, IGetUsersGroupMembershipResult } from '../../ts/ServerApi/requests/GetUsersGroupMembershipRequest';
import { IFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { IGetItemsParams } from '../../ts/ServerApi/types/IGetItemsParams';
import { IGroupMember } from '../../ts/ServerApi/types/IGroupMember';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ITypedIdWithName } from '../../ts/ServerApi/types/ITypedId';
import { ITypedIds } from '../../ts/ServerApi/types/ITypedIds';
import { UserKeys } from '../../ts/ServerApi/types/IUser';
import { EffectivePermissionsService } from '../../ts/services/EffectivePermissionsService';
import { LongRunningActionService } from '../../ts/services/LongRunningActionService';
import { SearchSuggestionsService } from '../../ts/services/SearchSuggestionsService';
import { ServerService } from '../../ts/services/ServerService';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { showRouterError } from '../actions/spaces/RouterErrorActions';
import { appStore } from '../AppStore';
import { InProgressRequestsTracker } from '../sagas/spaces/content/MixedContentSagas';
import { capabilitiesReact } from './Capabilities';
import { ALL_USERS_INDEX, PermissionsGrid, PermissionsGridRow } from './PermissionsGrid';
import { PermissionsLockUnlock } from './PermissionsLockUnlock';
import { CapabilitySections } from './SectionGroups';
import { AddNewPermissionStyles } from './style/AddNewPermissionStyle';
import { PermissionsDialogStyles } from './style/PermissionsDialogStyle';
import { PermissionsGridStyles } from './style/PermissionsGridStyle';
import { PermissionsLockUnlockStyles } from './style/PermissionsLockUnlockStyle';

export const PERMISSIONS_TABS_TESTID = 'permissions-dialog-tabs';

/**
 * Authorizable type
 */
export enum AuthType {
  PROJECT = 'project',
  PROJECT_DATASOURCE_TEMPLATE = 'projectDatasourceTemplate',
  PROJECT_DATA_ROLE_TEMPLATE = 'projectDataRoleTemplate',
  PROJECT_FLOW_TEMPLATE = 'projectFlowTemplate',
  PROJECT_METRIC_TEMPLATE = 'projectMetricTemplate',
  PROJECT_WORKBOOK_TEMPLATE = 'projectWorkbookTemplate',
  DATABASE_TABLE_TEMPLATE = 'databaseTableTemplate',
  WORKBOOK = 'workbook',
  VIEW = 'view',
  DATASOURCE = 'datasource',
  FLOW = 'flow',
  DATAROLE = 'dataRole',
  METRIC = 'metric',
  DATABASE = 'database',
  TABLE = 'table',
}

export function isAuthorizableContainer(authType: AuthType): boolean {
  return authType === AuthType.PROJECT || authType === AuthType.DATABASE;
}

const AUTH_TYPE: { [authType: string]: AuthType } = Object.freeze({
  [ProjectContentType.name]: AuthType.PROJECT,
  [WorkbookContentType.name]: AuthType.WORKBOOK,
  [ViewContentType.name]: AuthType.VIEW,
  [DatasourceContentType.name]: AuthType.DATASOURCE,
  [FlowContentType.name]: AuthType.FLOW,
  [DataRoleContentType.name]: AuthType.DATAROLE,
  [MetricContentType.name]: AuthType.METRIC,
  [DatabaseContentType.name]: AuthType.DATABASE,
  [TableContentType.name]: AuthType.TABLE,
});

const AuthTypeLabel: { [n: string]: () => string } = {
  [ProjectContentType.name]: Messages.projects,
  [AuthType.PROJECT_DATASOURCE_TEMPLATE]: Messages.datasources,
  [AuthType.PROJECT_DATA_ROLE_TEMPLATE]: Messages.dataRoles,
  [AuthType.PROJECT_FLOW_TEMPLATE]: Messages.flows,
  [AuthType.PROJECT_METRIC_TEMPLATE]: Messages.metrics,
  [AuthType.PROJECT_WORKBOOK_TEMPLATE]: Messages.workbooks,
  [AuthType.DATABASE_TABLE_TEMPLATE]: Messages.tables,
  [WorkbookContentType.name]: Messages.workbooks,
  [ViewContentType.name]: Messages.views,
  [DatasourceContentType.name]: Messages.datasources,
  [FlowContentType.name]: Messages.flows,
  [DataRoleContentType.name]: Messages.dataRoles,
  [MetricContentType.name]: Messages.metrics,
  [DatabaseContentType.name]: Messages.database,
  [TableContentType.name]: Messages.tables,
};

export type PermissionsDialogProps = {
  /** Original authorizables when dialog is first opened */
  authorizables: Set<any>;

  /** Type of authorizable this dialog is for */
  authorizableType: string;

  /** Width of the permissions container */
  permissionsContainerWidth: number;

  /** Height of the permissions panel */
  permissionsPanelHeight: number;

  /** Original explicit permissions values when the dialog is first opened */
  explicitPermissions: ExplicitPermissions;

  /** An instance of EffectivePermissionsService for requesting effective permissions */
  effectivePermissionsService: EffectivePermissionsService;

  /** An instance of SiteUsers for requesting site role for users */
  siteUsers: SiteUsers;

  /** The PermissionRoles that is injected into angular when opening the dialog */
  permissionRoles: PermissionRoles;

  /** The PermissionsEditabilityLogicFactory that is injected into angular when opening the dialog */
  permissionsEditabilityLogicFactory: PermissionsEditabilityLogicFactory;

  /** The Projects Resource that is injected into angular when opening the dialog */
  projectsResource: Projects;

  /** The Databases Resource that is injected into angular when opening the dialog */
  databasesResource: Databases;

  toasterService: ToasterService;

  longRunningActionService: LongRunningActionService;

  searchSuggestionsService: SearchSuggestionsService;

  serverService: ServerService;

  flowsEnabled: boolean;
};

export type PermissionsDialogState = {
  authorizables: Set<any>; // original values are passed in as props, but future updates are handled within the component
  editability: PermissionsEditability;
  effectivePermissions: ListResult<IGranteePermissions>;
  explicitPermissions: ListResult<IGranteePermissions>;
  isEditingPermissionRule: boolean;
  ruleSelectedFromSearch: boolean;
  selectedAuthTypeIndex: number;
  sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>;
};

export class PermissionsDialog extends React.Component<PermissionsDialogProps, PermissionsDialogState> {
  public static readonly PERMISSIONS_SEARCH_TEXTBOX_TESTID: string = 'searchForEffectivePermissionText';
  public readonly InputDebounceMilliseconds = 250;
  private authTypes: AuthType[] = null;
  private unspecifiedPermissions: _.Dictionary<_.Dictionary<IPermission>> = {};
  public explicitGrid: PermissionsGrid = null;
  public effectiveGrid: PermissionsGrid = null;
  public searchTextBoxRef: HTMLInputElement = null;
  private effectivePermissionsFilterClauses: IFilterClause[] = null;
  private dialogRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: PermissionsDialogProps) {
    super(props);
    this.setSectionGroups();
    this.createUnspecifiedPermissions();

    this.state = {
      editability: {
        editable: false,
        lockState: LockUnlockPermissionsState.Mixed,
        prompt: '',
        shortMessage: '',
        longMessage: '',
        sectionHeaderMessage: null,
      },
      authorizables: this.props.authorizables,
      explicitPermissions: null,
      effectivePermissions: null,
      ruleSelectedFromSearch: false,
      selectedAuthTypeIndex: 0,
      sourceAuthorizablesById: {},
      isEditingPermissionRule: false,
    };
  }

  public componentDidMount() {
    this.updateExplicitPermissionsAndEditability();
  }

  public componentDidUpdate(prevProps: PermissionsDialogProps, prevState: PermissionsDialogState, snapshot: any) {
    const dialogRef = this.dialogRef.current;
    if (snapshot !== null && dialogRef !== null && this.state.effectivePermissions !== null) {
      dialogRef.parentElement.scrollTop = snapshot;
    }
  }

  public getSnapshotBeforeUpdate(prevProps: PermissionsDialogProps, prevState: PermissionsDialogState) {
    // On smaller screens, the PermissionsDialog might have to scroll vertically. Any state change (such as getting the
    // next page of effective permissions) triggers a rerender, which causes the scroll position of the dialog to get
    // reset to the top. However, we wish to persist scroll position even after state changes, and returning the scroll
    // position value from getSnapshotBeforeUpdate and setting it as the new scroll value in componentDidUpdate is the
    // officially suggested React way to handle this behavior
    const dialogRef = this.dialogRef.current;
    if (dialogRef !== null) return dialogRef.parentElement.scrollTop;
    return null;
  }

  private setSectionGroups = (): void => {
    if (this.props.authorizableType === ProjectContentType.name) {
      this.authTypes = [AuthType.PROJECT, AuthType.PROJECT_WORKBOOK_TEMPLATE, AuthType.PROJECT_DATASOURCE_TEMPLATE, AuthType.PROJECT_DATA_ROLE_TEMPLATE];
      if (this.props.flowsEnabled) {
        this.authTypes.push(AuthType.PROJECT_FLOW_TEMPLATE);
      }
      if (FeatureFlags.metricsSpaceEnabled) {
        this.authTypes.push(AuthType.PROJECT_METRIC_TEMPLATE);
      }
    }

    if (this.props.authorizableType === DatabaseContentType.name) {
      this.authTypes = [AuthType.DATABASE, AuthType.DATABASE_TABLE_TEMPLATE];
    }
  }

  private isSessionSiteOrServerAdmin = (): boolean => {
    return this.props.serverService.isSessionSiteOrServerAdmin();
  }

  private createUnspecifiedPermissions = (): void => {
    PermissionsHelper.getPermissionsContentTypes(this.props.authorizableType, this.props.flowsEnabled).forEach(contentType => {
      this.unspecifiedPermissions[contentType] = PermissionsHelper.IPermissionsFromPermissions(
        this.props.permissionRoles.getPermissionsForTypeAndRole(contentType, SpecialRoleNames.None)
      );
    });
    if (AUTH_TYPE[this.props.authorizableType] === AuthType.PROJECT) {
      this.unspecifiedPermissions[AuthType.PROJECT][capabilitiesReact.projectLeader.name] = {
        permission: Permission.Unspecified,
        reason: PermissionReason.Unspecified,
      };
    }
  }

  private isAuthorizableCatalog = (): boolean => {
    const authType = AUTH_TYPE[this.props.authorizableType];
    return authType === AuthType.DATABASE || authType === AuthType.TABLE;
  }

  insufficientPermissionsCheck = (result: ExplicitPermissionsResult) => {
    // handle error if user has insufficient permissions to manage permissions
    if (result.errors && _.find(result.errors, { code: CommonErrorCode.CallingUserInsufficientPermissions })) {
      appStore.dispatch(showRouterError('insufficient-permissions'));
    }
  }

  setStateEditability = (sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>) => {
    // PermissionsEditabilityLogic generates a PermissionsEditability, which contains string values that are rendered to explain if permissions are locked
    const editabilityLogic: PermissionsEditabilityLogic = this.props.permissionsEditabilityLogicFactory.logicForAuthorizables(this.state.authorizables, this.props.authorizableType, sourceAuthorizablesById);
    this.setState({ editability: editabilityLogic.getEditabilityWithMessages(), sourceAuthorizablesById, });
  }

  private setMissingCapabilities = (explicitPermissions: ListResult<IGranteePermissions>) => {
    // BE doesn't store unspecified capabilities, so set all missing permissions to Unspecified.
    // This is needed to be able to know if a row in edit mode has been edited or has its original value
    PermissionsHelper.getPermissionsContentTypes(this.props.authorizableType, this.props.flowsEnabled).forEach(
      (contentType: string) => {
        const capabilities = _.keys(this.props.permissionRoles.getPermissionsForTypeAndRole(contentType, SpecialRoleNames.None));
        explicitPermissions.items.forEach((granteePermissions: IGranteePermissions) => {
          if (!_.has(granteePermissions.permissionsByContentType, contentType)) {
            granteePermissions.permissionsByContentType[contentType] = {};
          }
          const permissionsForContentType: _.Dictionary<IPermission> = granteePermissions.permissionsByContentType[contentType];
          capabilities.forEach((capability: string) => {
            if (!_.has(permissionsForContentType, capability)) {
              permissionsForContentType[capability] = {
                permission: Permission.Unspecified,
                reason: PermissionReason.Unspecified,
              };
            }
          });

          // project leader is no longer listed as a project capability, so have to handle it separately
          if (AUTH_TYPE[contentType] === AuthType.PROJECT && !permissionsForContentType[capabilitiesReact.projectLeader.name]) {
            permissionsForContentType[capabilitiesReact.projectLeader.name] = {
              permission: Permission.Unspecified,
              reason: PermissionReason.Unspecified,
            };
          }
        });
      }
    );

  }

  private setExplicitPermissions = (explicitPermissions: ListResult<IGranteePermissions>) => {
    explicitPermissions.items.sort(PermissionsHelper.compareGrantees);
    this.setMissingCapabilities(explicitPermissions);
    this.setState({ explicitPermissions });
  }

  private authorizableTypeIds = (): ITypedIds => {
    return { type: this.props.authorizableType, ids: this.state.authorizables.keys() };
  }

  private updateExplicitPermissionsAndEditability = (): IPromise<void | ExplicitPermissionsResult> => {
    // server request to get the explicit permissions list
    return this.props.explicitPermissions.forAuthorizables(this.authorizableTypeIds())
      .then((result: ExplicitPermissionsResult) => {
        if (result) {
          this.insufficientPermissionsCheck(result);
          this.setStateEditability(result.sourceAuthorizablesById);
          this.setExplicitPermissions(result.permissionsList);
        }
      }).then(() => {
        if (!this.explicitGrid.state.selectedRowIndex) {
          this.explicitGrid.setSelectedRow();
        }
      });
  }

  private getFilterClausesForEffectivePermissionsOnRowSelection = (selectedExplicitPermissionsRow: PermissionsGridRow):
    IFilterClause[] => {
    // Need an IFilterClause[] here because IGetItemParams requires an ICompositeFilterClause,
    // so even though the array will only contain one filter, we need it to make a composite filter
    let filterClauses: IFilterClause[] = [];

    // add a filter clause for the current group or user
    if (selectedExplicitPermissionsRow.isUser) {
      filterClauses.push(Filter.stringFieldEq('id', selectedExplicitPermissionsRow.granteeId));
    } else {
      filterClauses.push(Filter.stringArrayFieldHas('groupIds', selectedExplicitPermissionsRow.granteeId));
    }
    return filterClauses;
  }

  /**
   * Public - only for testing purpose
   */
  public getFilterClausesForEffectivePermissionsOnSearchTextChange = (searchText: string):
    IFilterClause[] => {
    let filterClauses: IFilterClause[] = [];

    // add a filter clause for the current search text
    if (searchText) {
      filterClauses.push(Filter.search(searchText));
    }
    return filterClauses;
  }

  private updateEffectivePermissions = (filterClauses: IFilterClause[], firstPage: boolean): void => {
    const page = firstPage ? { startIndex: 0, maxItems: 99, } : { startIndex: this.state.effectivePermissions.items.length, maxItems: 99, };
    if (firstPage) {
      InProgressRequestsTracker.clear();
    }
    if (InProgressRequestsTracker.isRequestInProgress(page.startIndex)) return;

    const effectivePermissionsFetchParams: IGetItemsParams = {
      filter: Filter.and(filterClauses),
      order: [{ field: UserKeys.displayName, ascending: true }],
      page: page,
    };

    const markRequestComplete = (): void => {
      InProgressRequestsTracker.markRequestComplete(page.startIndex);
    };

    const setEffectivePermissionInState = (updatedEffectivePermissions: ListResult<IGranteePermissions>): void => {
      // updatedEffectivePermissions are already sorted
      if (firstPage) {
        // getting the first page means we are getting effective permissions for a new query,
        // so replace any existing effective permissions
        this.setState({ effectivePermissions: updatedEffectivePermissions, }, () => {
          markRequestComplete();
          // then need to fetch group membership for users in updatedEffectivePermissions
          this.updateUsersGroupMembership(updatedEffectivePermissions);
        });
      } else {
        // getting a subsequent page means we are appending new results to the previous results
        this.setState((prevState) => {
          const combinedEffectivePermissions = new ListResult(prevState.effectivePermissions.items.concat(updatedEffectivePermissions.items), prevState.effectivePermissions.index, prevState.effectivePermissions.total);
          return { effectivePermissions: combinedEffectivePermissions, };
        }, () => {
          markRequestComplete();
          // then need to fetch group membership for users in updatedEffectivePermissions
          this.updateUsersGroupMembership(updatedEffectivePermissions, true);
        });
      }
    };

    InProgressRequestsTracker.markRequestInProgress(page.startIndex);
    if (this.isSessionSiteOrServerAdmin()) {
      // If current user is an admin then we need to show site role in effective permission grid
      this.props.effectivePermissionsService.getEffectivePermissionsWithSiteRole(
        effectivePermissionsFetchParams,
        this.authorizableTypeIds())
        .then(setEffectivePermissionInState);
    } else {
      this.props.effectivePermissionsService.getEffectivePermissions(effectivePermissionsFetchParams, this.authorizableTypeIds())
        .then(setEffectivePermissionInState);
    }
  }

  private updateUsersGroupMembership(updatedEffectivePermissions: ListResult<IGranteePermissions>, appendResults?: boolean): void {
    const userIds = updatedEffectivePermissions.items.map((item: IGranteePermissions) => item.user.id);
    if (userIds.length) {
      // get the ids of the groups that each user is a member of
      this.props.serverService.sendRequest(new GetUsersGroupMembershipRequest({ userIds }))
        .then((result: IGetUsersGroupMembershipResult) => {
          let updatedUsersGroupMembership: _.Dictionary<string[]> = {};
          result.members.forEach((member: IGroupMember) => {
            let userGroups = updatedUsersGroupMembership[member.userId];
            if (!userGroups) {
              updatedUsersGroupMembership[member.userId] = [member.groupId];
            } else {
              userGroups.push(member.groupId);
            }
          });
          this.explicitGrid && this.explicitGrid.setRelevantGroupMembership(updatedUsersGroupMembership, appendResults);
        });
    } else {
      this.explicitGrid && this.explicitGrid.setRelevantGroupMembership(undefined, appendResults);
    }
  }

  private handleRowSelect = (rowData: PermissionsGridRow, selectedFromEffectivePermissions: boolean): void => {
    if (selectedFromEffectivePermissions) {
      this.explicitGrid && this.explicitGrid.setRelevantUserId(rowData ? rowData.granteeId : undefined);
    } else {
      if (!rowData) {
        // if no rowData, then that means highlighting of relevant rules was blocking normal row selection,
        // and selection in the effective grid needs to be cleared.
        this.effectiveGrid && this.effectiveGrid.clearSelectedRow();
        return;
      }
      // When user is searching for effective permission using the search text box, we select All Users row automatically
      // don't update the effective permission grid in that case. 
      // If searchbox is not empty then search is in progress
      if (rowData.index === ALL_USERS_INDEX && this.getSearchTextBoxText()) return;

      this.setState((prevState) => {
        let newState = {
          ...(prevState.ruleSelectedFromSearch && { ruleSelectedFromSearch: false }),
        };

        return newState;
      });
      this.effectivePermissionsFilterClauses = this.getFilterClausesForEffectivePermissionsOnRowSelection(rowData);
      this.updateEffectivePermissions(this.effectivePermissionsFilterClauses, true);
    }
  }

  private getNextPermissionsPage = (): void => {
    this.updateEffectivePermissions(this.effectivePermissionsFilterClauses, false);
  }

  private saveExplicitPermissionEdits = (permissions: IGranteePermissions): IPromise<void | IResult> => {
    // server request to set the provided permissions for the authorizables
    return this.props.explicitPermissions.setForAuthorizables({ type: this.props.authorizableType, ids: this.state.authorizables.keys() }, permissions)
      .then((result: IResult) => {
        this.updateExplicitPermissionsAndEditability().then(() => {
          const granteeName = L10n.elideIfLong(PermissionsHelper.granteeName(permissions));
          if (result.errors && result.errors.length) {
            this.props.toasterService.pop({
              type: ToastType.Error,
              text: Messages.permissionRuleChangeFailed({ GRANTEE_NAME: granteeName, }),
            });
          } else {
            this.props.toasterService.pop({
              type: ToastType.Info,
              text: this.isAuthorizableCatalog() ? Messages.permissionRuleChangedSuccessWithWait({ GRANTEE_NAME: granteeName, }) : Messages.permissionRuleChangedSuccess({ GRANTEE_NAME: granteeName, }),
            });
          }
        });
      });
  }

  private updateLockState = (newLockState: LockUnlockPermissionsState, newNestedProjectsState?: NestedProjectsPermissionsState): void => {
    this.setState((state) => {
      const updatedControlledPermissions: boolean = newLockState === LockUnlockPermissionsState.Locked;
      const updatedNestedProjectsPermissions: boolean = newNestedProjectsState === NestedProjectsPermissionsState.Included;
      let authorizables: Set<any> = state.authorizables.clone();
      if (this.props.authorizableType === AuthType.PROJECT) {
        authorizables.each((project: IProjectInfo) => {
          if (project.controlledPermissionsEnabled !== updatedControlledPermissions) {
            project.controlledPermissionsEnabled = updatedControlledPermissions;
          }
          project.nestedProjectsPermissionsIncluded = updatedNestedProjectsPermissions;
        });
      } else if (this.props.authorizableType === AuthType.DATABASE) {
        authorizables.each((database: any) => {
          if (database.controlledPermissionsEnabled !== updatedControlledPermissions) {
            database.controlledPermissionsEnabled = updatedControlledPermissions;
          }
        });
      }

      return { authorizables, };
    });
  }

  private handleEditModeChange = (isEditingPermissionRule: boolean): void => {
    this.setState({ isEditingPermissionRule, });
  }

  private setExplicitGridRef = (element: PermissionsGrid): void => {
    this.explicitGrid = element;
  }

  private setEffectiveGridRef = (element: PermissionsGrid): void => {
    this.effectiveGrid = element;
  }

  private renderPermissionsContent = (permissions: ListResult<IGranteePermissions>, authType: AuthType, isExplicitPermissions: boolean, containerAuthType?: AuthType): JSX.Element => {
    return (
      <PermissionsGrid
        permissionRoles={this.props.permissionRoles}
        permissions={permissions}
        capabilitySections={CapabilitySections[authType]()}
        authorizableType={authType}
        containerAuthorizableType={containerAuthType}
        isExplicitPermissions={isExplicitPermissions}
        isSessionSiteOrServerAdmin={this.isSessionSiteOrServerAdmin()}
        isEditable={isExplicitPermissions ? this.state.editability.editable : null}
        unspecifiedPermissions={isExplicitPermissions ? this.unspecifiedPermissions : null}
        onRowSelect={this.handleRowSelect}
        savePermissionEdits={this.saveExplicitPermissionEdits}
        onEditModeChange={isExplicitPermissions ? this.handleEditModeChange : null}
        disableNonEditingRows={this.state.isEditingPermissionRule}
        searchSuggestionsService={this.props.searchSuggestionsService}
        selectedFromSearch={isExplicitPermissions && this.state.ruleSelectedFromSearch}
        ref={isExplicitPermissions ? this.setExplicitGridRef : this.setEffectiveGridRef}
        getNextPermissionsPage={!isExplicitPermissions && this.getNextPermissionsPage}
        getSearchTextBoxText={this.getSearchTextBoxText}
        clearSearchTextBoxText={this.clearSearchTextBoxText}
      />
    );
  }

  private templateTabs = (): TabAndPanelContents[] => {
    return this.authTypes.map((authType: AuthType): TabAndPanelContents => {
      return {
        tabContent: { label: AuthTypeLabel[authType]() },
        panelContent: null,
      };
    });
  }

  private onTabIndexChanged = (index: number) => {
    this.setState({ selectedAuthTypeIndex: index, });
  }

  /**
   * Public - only for testing purpose
   */
  public onSearchTextForEffectivePermissionChange = (searchText: string) => {
    this.setState((prevState) => {
      if (prevState.ruleSelectedFromSearch) {
        return null;
      } else {
        return { ruleSelectedFromSearch: true, };
      }
    });
    if (this.explicitGrid) this.explicitGrid.setSelectedRow();
    this.effectivePermissionsFilterClauses = this.getFilterClausesForEffectivePermissionsOnSearchTextChange(searchText);
    this.updateEffectivePermissions(this.effectivePermissionsFilterClauses, true);
  }

  private debounceOnSearchTextChange = _.debounce(this.onSearchTextForEffectivePermissionChange,
    this.InputDebounceMilliseconds);

  private renderExplicitPermissions = (): JSX.Element => {
    switch (this.props.authorizableType) {
      case ProjectContentType.name:
      case DatabaseContentType.name:
        // permissions content is outside of TabsWidget for styling reasons (grid extends beyond the tabs)
        // and logic reasons (the grid knows all of the permissions across all authorizable types, but
        // internally chooses which to display based on selected tab)
        return (
          <>
            <div className={PermissionsDialogStyles.TabsContainer}>
              <div className={PermissionsDialogStyles.TabsBottom} />
              <TabsWidget
                activationMode={TabActivationMode.Manual}
                anchorTabIndex={0}
                ariaLabel={Messages.permissions()}
                initialActiveIndex={0}
                onActiveIndexChanged={this.onTabIndexChanged}
                tabs={this.templateTabs()}
                testId={PERMISSIONS_TABS_TESTID}
              />
            </div>
            {this.renderPermissionsContent(this.state.explicitPermissions, this.authTypes[this.state.selectedAuthTypeIndex], true, AUTH_TYPE[this.props.authorizableType])}
          </>
        );
      default:
        return this.renderPermissionsContent(this.state.explicitPermissions, AUTH_TYPE[this.props.authorizableType], true);
    }
  }

  private renderEffectivePermissions = (): JSX.Element => {
    let authType: AuthType;
    switch (this.props.authorizableType) {
      case ProjectContentType.name:
      case DatabaseContentType.name:
        authType = this.authTypes[this.state.selectedAuthTypeIndex];
        break;
      default:
        authType = AUTH_TYPE[this.props.authorizableType];
    }
    return this.renderPermissionsContent(this.state.effectivePermissions, authType, false);
  }

  private renderTitleHeader = (text: string, disabled: boolean, isTopmostComponent?: boolean): JSX.Element => {
    let className;
    if (disabled) {
      if (isTopmostComponent) {
        className = PermissionsDialogStyles.TitleHeaderClassTopDisabled;
      } else {
        className = PermissionsDialogStyles.TitleHeaderClassDisabled;
      }
    } else {
      if (isTopmostComponent) {
        className = PermissionsDialogStyles.TitleHeaderClassTop;
      } else {
        className = PermissionsDialogStyles.TitleHeaderClass;
      }
    }
    return <div className={className}>{text}</div>;
  }

  private setPermissionsSearchTextBoxRef = (element: HTMLInputElement): void => {
    this.searchTextBoxRef = element;
  }

  public clearSearchTextBoxText = (): void => {
    this.searchTextBoxRef.value && (this.searchTextBoxRef.value = '');
  }

  public getSearchTextBoxText = (): string => {
    return this.searchTextBoxRef.value;
  }

  private renderEffectivePermissionsSearch = (): JSX.Element => {
    const searchFieldProps: TextFieldProps = {
      kind: 'search',
      label: '',
      onChange: (e) => this.debounceOnSearchTextChange(e.target.value),
      testId: PermissionsDialog.PERMISSIONS_SEARCH_TEXTBOX_TESTID,
      className: PermissionsDialogStyles.SearchTextboxContainer,
    };

    return (
      <TextFieldWidget
        {...searchFieldProps}
        disabled={this.state.isEditingPermissionRule}
        ref={this.setPermissionsSearchTextBoxRef}
        placeholder={Messages.searchUserToViewEffectivePermission()}
      />
    );
  }

  private shouldRenderPermissionsLockUnlock = (): boolean => {
    // Only render the PermissionsLockUnlock component if authorizableType is project or database,
    // or if there is a short message to display (e.g.,
    // when the parent project permissions are locked and the message might say "Locked to parent project")
    return isAuthorizableContainer(AUTH_TYPE[this.props.authorizableType]) || this.state.editability.shortMessage !== '';
  }

  private renderPermissionsLockUnlock = (): JSX.Element => {
    return <PermissionsLockUnlock
      authorizables={this.state.authorizables}
      authorizableType={AUTH_TYPE[this.props.authorizableType]}
      databasesResource={this.props.databasesResource}
      longRunningActionService={this.props.longRunningActionService}
      nonProjectMessage={this.state.editability.shortMessage}
      projectsResource={this.props.projectsResource}
      serverService={this.props.serverService}
      sourceAuthorizablesById={this.state.sourceAuthorizablesById}
      toasterService={this.props.toasterService}
      updateLockStatus={this.updateLockState}
    />;
  }

  private calculateDialogHeight = (): number => {
    // unknown at the time of first opening the dialog if we need to show the lock message, so just always include
    // it in the height calculation so the dialog centering does not get ruined when the height changes
    let lockMessage = PermissionsLockUnlockStyles.permissionsLockUnlockHeight + PermissionsDialogStyles.titleMarginTop;
    let permissionsRulesHeader = PermissionsDialogStyles.lineHeight + PermissionsDialogStyles.titleMarginBottom; // permission rules header
    let sectionAboveGrids = lockMessage + permissionsRulesHeader;
    if (isAuthorizableContainer(AUTH_TYPE[this.props.authorizableType])) {
      sectionAboveGrids += PermissionsDialogStyles.tabsHeight + PermissionsDialogStyles.tabsBottomHeight + PermissionsDialogStyles.tabsMarginBottom;
    }
    const gridHeight = PermissionsGridStyles.RowHeight * (PermissionsGridStyles.MaxRows + 1) + 2;

    // also unknown when first opening the dialog is if the grid is editable, so need to always
    // include the height of the add new permissions button, which only appears if it is editable
    let addNewPermissionsHeight = AddNewPermissionStyles.ButtonTopMargin + TabStyles.Sizing.RowHeight; // button widget uses rowheight for its height
    let sectionBetweenGrids = addNewPermissionsHeight + PermissionsDialogStyles.titleMarginTop + PermissionsDialogStyles.lineHeight + PermissionsDialogStyles.searchMarginTop + 24 + PermissionsDialogStyles.searchMarginBottom + 18;
    return sectionAboveGrids + gridHeight + sectionBetweenGrids + gridHeight;
  }

  public render() {
    const shouldRenderPermissionsLockUnlock = this.shouldRenderPermissionsLockUnlock();

    // height has to be set here to a number so that when the dialog first opens it is properly centered
    const height = this.calculateDialogHeight();

    return (
      <div className={PermissionsDialogStyles.DialogContents}
        style={{ height, maxHeight: height, overflowX: 'inherit', width: this.props.permissionsContainerWidth - 2 * PermissionsDialogStyles.containerPadding }}
        ref={this.dialogRef}>

        {shouldRenderPermissionsLockUnlock && this.renderPermissionsLockUnlock()}
        {this.renderTitleHeader(Messages.permissionRules(), false, !shouldRenderPermissionsLockUnlock)}
        {this.renderExplicitPermissions()}
        {this.renderTitleHeader(Messages.effectivePermissions(), this.state.isEditingPermissionRule)}
        {this.renderEffectivePermissionsSearch()}
        {this.renderEffectivePermissions()}
      </div>
    );
  }
}
